import { createTheme } from "@mui/material/styles"
import { baseCardTheme } from "./TeamBaseCardTheme"

// Preview card theme - PROPERLY SCALED CardDesign5 for preview with ALL VALUES CENTRALIZED
export const cardDesign5PreviewTheme = createTheme(
    {
        ...baseCardTheme,
        card: {
            container: {
                maxWidth: {
                    xs: "90%",
                    sm: "280px",
                    md: "350px",
                    lg: "400px",
                    xl: "450px",
                },
                padding: "10px",
            },
            profileImage: {
                size: "120px",
                border: "3px solid #ffffff",
                bottom: "-60px",
                padding: "1px",
                margin: "-4px",
            },
            companyLogo: {
                size: "45px",
                bottom: "-55px",
                padding: "3px",
                margin: "-3px",
                rightOffset: "calc(50% - 75px)",
                leftOffset: "90px", // For left-aligned layout
            },
            portraitLogo: {
                size: "45px",
                top: "40px",
                right: "-40px",
            },
            content: {
                marginTop: {
                    portrait: "15px",
                    other: "30px",
                },
                padding: "15px",
            },
            typography: {
                nameSize: "1.2rem",
                nameHeight: "28px",
                nameLineHeight: "28px",
                nameMarginBottom: "2px",

                pronounSize: "14px",
                pronounHeight: "20px",
                pronounLineHeight: "20px",
                pronounMarginTop: "6px",

                jobTitleSize: "0.9rem",
                jobTitleLineHeight: "1.4rem",

                locationSize: "0.85rem",
                locationLineHeight: "1.5rem",

                bioSize: {
                    xs: "0.85rem",
                    sm: "1rem",
                    md: "1.2rem",
                },
                bioLineHeight: {
                    xs: "4vh",
                    sm: "5vh",
                    md: "18px",
                },
                bioMargin: {
                    top: "1rem",
                    bottom: {
                        xs: "0",
                        sm: "0.3rem",
                        md: "0.7rem",
                    },
                },
            },
            button: {
                fontSize: "0.95rem",
                padding: "0.5rem 0.8rem",
                letterSpacing: "0.02rem",
                marginTop: "15px",
            },
            links: {
                marginTop: 1.5,
                itemMargin: 1,
                itemMarginBottom: 0.5,
                iconSize: "40px",
                iconBorderRadius: "10px",
                iconPadding: "3px",
                leftIconSize: "35px",
                leftIconBorderRadius: "5px",
                titleFontSize: "10px",
                leftTitleFontSize: "12px",
                titleMarginTop: "3px",
                iconMarginRight: 1,
                rowMarginBottom: 1,
                itemMaxWidth: "80px",
                itemPadding: "0 3px",
            },
            // CardDesign5 for preview mode with CSS transform scaling - ALL VALUES CENTRALIZED
            cardDesign5: {
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',

                // Color scheme - SAME AS FULL THEME
                colors: {
                    primary: "#FF7A00",
                    secondary: "#00796B",
                    profileCard: "#006659",
                    white: "#fff",
                    black: "#000",
                    greyBackground: "#f6f6f6",
                    greyLight: "#f5f5f5",
                    greyText: "#999",
                    greyDark: "#666",
                    textDark: "rgb(31, 41, 55)",
                    textMedium: "rgb(107, 114, 128)",
                    textLight: "rgb(75, 85, 99)",

                    // Social media brand colors
                    facebook: "#1877F2",
                    twitter: "#1DA1F2",
                    linkedin: "#0077B5",
                    instagram: "#E4405F",
                    wechat: "#07C160",

                    // Transparency values
                    whiteTransparent: "rgba(0, 0, 0, 0.1)",
                    blackTransparent: "rgba(0, 0, 0, 0.15)",
                    blackTransparentLight: "rgba(0, 0, 0, 0.08)",
                    overlayLight: "rgba(0, 121, 107, 0.04)",
                },

                // Numeric values - SAME AS FULL THEME
                values: {
                    darkenAmount: 0.2,
                    lightenAmount: 0.15,
                    profileCardWidthLeft: "45%",
                    profileCardWidthRight: "55%",
                    addressMarginTop: 0.5,
                    profileCardOpacity: 0.9,
                    profileCardPronounOpacity: 0.8,
                    profileCardNameLineHeight: 1.2,
                    profileCardNameMarginBottom: 0.5,
                    profileCardJobTitleMarginBottom: 0.5,
                    tabContentPaddingBottom: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                    actionButtonsGap: 2,
                    productItemMarginBottom: 2,
                    emptyMessagePadding: { x: 2, y: 4 },
                    sectionTitleMarginTop: 3,
                },

                // CSS properties - SAME AS FULL THEME 
                cssProperties: {
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    objectFit: "contain",
                    cursor: "pointer",
                    textTransform: "none",
                    fontFamily: '"Kumbh Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    transition: "all 0.2s ease-in-out",
                    wordBreak: "break-word",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textAlign: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    display: "flex",
                    position: "relative",
                    zIndex: { low: 1, medium: 10, high: 15, highest: 20 },
                    transparent: "transparent",
                    none: "none",
                },

                // Fallback text values - SAME AS FULL THEME
                fallbackText: {
                    fullName: "Full Name",
                    jobTitle: "Job Title",
                    companyInitial: "C",
                    jobTitleLabel: "JOB TITLE",
                    companyNameLabel: "COMPANY NAME",
                    addressLabel: "ADDRESS",
                    contactSectionTitle: "CONTACT",
                    aboutSectionTitle: "ABOUT",
                    phoneLabel: "PHONE",
                    twitterLabel: "X (TWITTER)",
                    noSocialLinks: "No social links added yet.",
                    noProducts: "No products or services added yet.",
                    saveContactButton: "Save Contact",
                    connectButton: "Connect",
                    companyLogoAlt: "Company Logo",
                },

                // Main container styles for preview - MADE WIDER
                mainContainer: {
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    // UPDATED: Made wider for preview
                    maxWidth: { xs: "100%", sm: "480px", md: "540px" },
                    margin: "auto",
                    bgcolor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    position: "relative",
                    // AUTOMATIC SCALING - Scale down the entire component
                    transform: "scale(0.9)",
                    transformOrigin: "top center",
                },

                // Header background styles - INCREASED HEIGHT
                headerBackground: {
                    height: { xs: "13.75rem", sm: "15rem", md: "16.25rem" },
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                },

                // Header content styles
                headerContent: {
                    padding: { xs: 1.5, sm: 2 },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                },

                // Company logo in header
                companyLogo: {
                    width: { xs: "2.5rem", sm: "2.8125rem", md: "3.125rem" },
                    height: { xs: "2.5rem", sm: "2.8125rem", md: "3.125rem" },
                    bgcolor: "#fff",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    border: "2px solid #fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                },

                // Company initial text
                companyInitial: {
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                    fontFamily: "sans-serif",
                },

                // QR Code icon - PREVIEW: NON-INTERACTIVE
                qrIcon: {
                    width: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                    height: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                    bgcolor: "#fff",
                    borderRadius: { xs: "0.375rem", sm: "0.5rem", md: "0.625rem" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1.5px solid #fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    // Remove cursor and transition for preview
                    cursor: "default",
                    pointerEvents: "none"
                },

                // QR icon inner
                qrIconInner: {
                    color: "#000",
                    fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
                },

                // QR icon hover effect - PREVIEW: REMOVED HOVER EFFECTS
                qrIconHover: {
                    // Remove hover effects for preview
                },

                // White background section for preview
                whiteBackground: {
                    bgcolor: "#fff",
                    minHeight: { xs: "25rem", sm: "28.125rem", md: "31.25rem" },
                    position: "relative",
                    borderTopLeftRadius: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                    borderTopRightRadius: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                    marginTop: { xs: "-0.75rem", sm: "-0.875rem", md: "-1rem" },
                    paddingTop: { xs: "3.75rem", sm: "4.375rem", md: "5rem" },
                    zIndex: 1,
                },

                // Profile card positioning and sizing for preview - MOVED UP & REDUCED HEIGHT
                profileCard: {
                    position: "absolute",
                    willChange: "transform",
                    backfaceVisibility: "hidden",
                    // UPDATED: Moved up so top half is on cover photo, bottom half on white container
                    top: { xs: "-5rem", sm: "-5.5rem", md: "-6rem" },
                    left: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                    right: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                    // UPDATED: Reduced height for preview only
                    height: { xs: "8rem", sm: "9rem", md: "10rem" },
                    borderRadius: { xs: "0.75rem", sm: "1rem", md: "1.25rem" },
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    display: "flex",
                    zIndex: 10,
                },

                // Profile photo section (left side)
                profilePhotoSection: {
                    width: "45%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    position: "relative",
                    bgcolor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },

                // Fallback avatar - ADJUSTED SIZE FOR PREVIEW
                fallbackAvatar: {
                    width: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                    height: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                    border: "3px solid #fff",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },

                // Avatar image styles
                avatarImageStyles: {
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                },

                // Profile card right section
                profileCardRightSection: {
                    width: "55%",
                    padding: { xs: 1, sm: 1.5, md: 2 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                },

                // Name typography in profile card - ADJUSTED FOR SMALLER HEIGHT
                profileCardName: {
                    fontWeight: "bold",
                    lineHeight: 1.1,
                    marginBottom: 0.3,
                    fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                    wordBreak: "break-word",
                },

                // Job title typography in profile card - ADJUSTED FOR SMALLER HEIGHT
                profileCardJobTitle: {
                    marginBottom: 0.3,
                    fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.8rem" },
                    wordBreak: "break-word",
                },

                // Pronoun typography in profile card
                profileCardPronoun: {
                    fontSize: { xs: "0.55rem", sm: "0.625rem", md: "0.7rem" },
                },

                // Secondary job title below profile card
                secondaryJobTitle: {
                    paddingTop: { xs: "0.25rem", sm: "0.5rem", md: "0.75rem" },
                    paddingBottom: 1,
                    textAlign: "center"
                },

                secondaryJobTitleText: {
                    fontFamily: '"Kumbh Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    fontWeight: 500,
                    fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                },

                // Tabs container
                tabsContainer: {
                    backgroundColor: "#f6f6f6",
                    borderRadius: "8px",
                    padding: { xs: "0.25rem", sm: "0.375rem", md: "0.5rem" },
                    margin: { xs: "0.25rem 1.5rem", sm: "0.25rem 2rem", md: "0.375rem 2.5rem" },
                },

                // Tabs styling - OPTIMIZED: Even smaller font & better spacing for preview
                tabs: {
                    "& .MuiTabs-indicator": {
                        display: "none",
                    },
                    "& .MuiTab-root": {
                        backgroundColor: "transparent",
                        color: "#000000",
                        borderRadius: "8px",
                        margin: "2px",
                        textTransform: "none",
                        fontFamily: '"Kumbh Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 600,
                        // UPDATED: Even smaller font size for preview to prevent cut-off
                        fontSize: { xs: "0.45rem", sm: "0.5rem", md: "0.6rem" },
                        minWidth: "auto",
                        flexGrow: 1,
                        whiteSpace: "nowrap",
                        // Reduced padding for preview
                        padding: { xs: "0.25rem 0.1rem", sm: "0.3rem 0.15rem", md: "0.35rem 0.2rem" },
                        "&.Mui-selected": {
                            backgroundColor: "#ffffff",
                            color: "rgb(31, 41, 55)",
                            fontWeight: 600,
                        },
                        "&:hover": {
                            backgroundColor: "#ffffff",
                            color: "rgb(31, 41, 55)",
                            transform: "scale(1.02)",
                        },
                    },
                    "& .MuiTabs-flexContainer": {
                        gap: { xs: "0.125rem", sm: "0.25rem", md: "0.375rem" },
                    },
                    // OPTIMIZED: Much smaller scroll buttons for preview
                    "& .MuiTabs-scrollButtons": {
                        // UPDATED: Much smaller width for preview
                        width: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                        // UPDATED: Minimal padding
                        paddingX: { xs: "0.0625rem", sm: "0.125rem", md: "0.1875rem" },
                        color: "#666",
                        opacity: 0.7,
                        borderRadius: "4px",
                        transition: "all 0.2s ease-in-out",
                        "&.Mui-disabled": {
                            opacity: 0.3,
                        },
                        "&:hover": {
                            opacity: 1,
                            backgroundColor: "rgba(0,0,0,0.04)",
                            transform: "scale(1.05)",
                        },
                        "& .MuiSvgIcon-root": {
                            // UPDATED: Much smaller arrow icons for preview
                            fontSize: { xs: "0.625rem", sm: "0.75rem", md: "0.875rem" },
                        }
                    },
                    "& .MuiTabs-scroller": {
                        scrollBehavior: "smooth",
                        "&::-webkit-scrollbar": {
                            display: "none", // Hide scrollbar for cleaner look
                        },
                        scrollbarWidth: "none", // Firefox
                    },
                    // Scrollable mode adjustments for preview
                    "&[data-scrollable='true']": {
                        "& .MuiTabs-flexContainer": {
                            // UPDATED: Minimal gap and padding for maximum tab space
                            gap: { xs: "0.0625rem", sm: "0.125rem" },
                            paddingX: { xs: "0.125rem", sm: "0.25rem", md: "0.375rem" },
                        },
                        "& .MuiTab-root": {
                            flexGrow: 0,
                            flexShrink: 0,
                            minWidth: "auto",
                            // UPDATED: Minimal padding for maximum text space in preview
                            paddingX: { xs: "0.25rem", sm: "0.375rem", md: "0.5rem" },
                        }
                    }
                },

                // Individual tab styling
                tabItem: {
                    minWidth: { xs: "3rem", sm: "4rem", md: "5rem" },
                    flex: "0 1 auto",
                },

                tabItemLarge: {
                    minWidth: { xs: "6rem", sm: "8rem", md: "10rem" },
                    flex: "0 1 auto",
                },

                // Tab content
                tabContent: {
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingBottom: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                },

                // Contact tab container
                contactTabContainer: {
                    padding: 0,
                },

                // Contact item (grey containers)
                contactItem: {
                    backgroundColor: "#f6f6f6",
                    margin: { xs: "0.25rem 1.5rem", sm: "0.375rem 2rem", md: "0.5rem 2.5rem" },
                    borderRadius: "8px",
                    padding: { xs: "0.5rem", sm: "0.625rem", md: "0.75rem" },
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                },

                // Contact item for address (different alignment)
                contactItemAddress: {
                    backgroundColor: "#f6f6f6",
                    margin: { xs: "0.25rem 1.5rem", sm: "0.375rem 2rem", md: "0.5rem 2.5rem" },
                    borderRadius: "8px",
                    padding: { xs: "0.5rem", sm: "0.625rem", md: "0.75rem" },
                    display: "flex",
                    alignItems: "center", // UPDATED: Changed from flex-start to center for consistent alignment
                    cursor: "pointer",
                },

                // Contact icon - FIXED: PROPER SIZING FOR PERFECT CIRCLES IN PREVIEW
                contactIcon: {
                    width: 24,
                    height: 24,
                    minWidth: 24, // ADDED: Prevent compression
                    minHeight: 24, // ADDED: Prevent compression
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 2,
                    flexShrink: 0, // ADDED: Prevent shrinking
                },

                // Contact icon for address (with top margin) - FIXED: PROPER SIZING AND ALIGNMENT
                contactIconAddress: {
                    width: 24,
                    height: 24,
                    minWidth: 24, // ADDED: Prevent compression
                    minHeight: 24, // ADDED: Prevent compression
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 2,
                    marginTop: 0, // UPDATED: Removed top margin for consistent alignment
                    flexShrink: 0, // ADDED: Prevent shrinking
                },

                // Contact icon for larger social icons - FIXED: PROPER SIZING
                contactIconLarge: {
                    width: 32,
                    height: 32,
                    minWidth: 32, // ADDED: Prevent compression
                    minHeight: 32, // ADDED: Prevent compression
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 2,
                    flexShrink: 0, // ADDED: Prevent shrinking
                },

                // Contact label
                contactLabel: {
                    color: "#666",
                    display: "block",
                    fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.75rem" },
                    fontWeight: 500,
                    letterSpacing: "0.03em",
                },

                // Contact value - PREVIEW: WITH ELLIPSIS FOR ALL CONTACT TYPES
                contactValue: {
                    fontWeight: 600,
                    fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                    // PREVIEW SPECIFIC: Use ellipsis for all contact types
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                    minWidth: 0,
                },

                // Section title
                sectionTitle: {
                    color: "#666",
                    textTransform: "uppercase",
                    fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
                    letterSpacing: "0.05em",
                    fontWeight: 600,
                    marginTop: 3,
                    textAlign: "center",
                },

                // Product section
                productSection: {
                    padding: 0,
                },

                // Product section content
                productSectionContent: {
                    paddingLeft: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                    paddingRight: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                },

                // Product item margin
                productItem: {
                    marginBottom: 2,
                },

                // Empty state message
                emptyMessage: {
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 2,
                    color: "#999",
                    fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                },

                // Action buttons container
                actionButtons: {
                    backgroundColor: "#fff",
                    padding: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                    display: "flex",
                    gap: 2,
                    marginTop: "auto",
                },

                // Save button - PREVIEW: NON-INTERACTIVE
                saveButton: {
                    flex: 1,
                    borderRadius: "8px",
                    paddingY: { xs: 1.5, sm: 1.75, md: 2 },
                    color: "#fff",
                    // REMOVED: cursor pointer and hover effects for preview
                    pointerEvents: "none", // ADDED: Make non-interactive in preview
                },

                // Connect button - PREVIEW: NON-INTERACTIVE
                connectButton: {
                    flex: 1,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    paddingY: 1,
                    paddingX: 2,
                    color: "#fff",
                    // REMOVED: cursor pointer and hover effects for preview
                    pointerEvents: "none", // ADDED: Make non-interactive in preview
                },

                // Icon styles - FIXED: SMALLER ICON SIZE FOR PREVIEW TO FIT PROPERLY IN CIRCLES
                iconStyles: {
                    color: "#fff",
                    fontSize: "0.8rem", // REDUCED from 1rem for better fit in 24x24 circles
                },

                // Social icon styles - FIXED: SMALLER SOCIAL ICON SIZE FOR PREVIEW
                socialIconStyles: {
                    color: "#fff",
                    fontSize: "1rem", // REDUCED from 1.25rem for better fit in 32x32 containers
                },

                // Social icon image
                socialIconImage: {
                    width: "100%",
                    height: "100%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: "3px",
                },

                // Hover effects
                hoverTransform: {
                    "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                },

                // Map styles - PREVIEW: SHORTER HEIGHT
                map: {
                    container: {
                        height: 120, // REDUCED from 200 for preview
                        border: '1px solid #ddd',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                    image: {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    },
                    overlay: {
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#1976d2',
                        padding: '4px 8px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }
                },
            },
        },
    },
    baseCardTheme,
);